import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { TaskItem } from "@tiptap/extension-task-item";
import { TaskList } from "@tiptap/extension-task-list";
import { Markdown } from "@tiptap/markdown";
import { PluginKey } from "@tiptap/pm/state";
import {
  type Editor,
  EditorContent,
  Extension,
  type Range,
  ReactRenderer,
  useEditor,
  useEditorState,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Suggestion, { type SuggestionProps } from "@tiptap/suggestion";
import {
  Bold,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  Link2,
  List,
  ListChecks,
  ListOrdered,
  type LucideIcon,
  Minus,
  Quote,
  SquareCode,
  Strikethrough,
} from "lucide-react";
import { forwardRef, type ReactNode, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Toggle } from "../ui/toggle";
import { markdownProse } from "./markdown";

const MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
const MOD = MAC ? "⌘" : "Ctrl";
const ALT = MAC ? "⌥" : "Alt";
const SHIFT = MAC ? "⇧" : "Shift";

function keys(...parts: string[]): string {
  return MAC ? parts.join("") : parts.join("+");
}

type SlashItem = {
  title: string;
  keywords: string;
  icon: LucideIcon;
  shortcut?: string;
  group: string;
  run: (args: { editor: Editor; range: Range }) => void;
};

function block(editor: Editor, range: Range, apply: (chain: ReturnType<Editor["chain"]>) => void) {
  const chain = editor.chain().focus().deleteRange(range);
  apply(chain);
  chain.run();
}

const SLASH_ITEMS: SlashItem[] = [
  {
    title: "Heading 1",
    keywords: "h1 title",
    icon: Heading1,
    shortcut: keys(MOD, ALT, "1"),
    group: "text",
    run: ({ editor, range }) => block(editor, range, (chain) => chain.setHeading({ level: 1 })),
  },
  {
    title: "Heading 2",
    keywords: "h2",
    icon: Heading2,
    shortcut: keys(MOD, ALT, "2"),
    group: "text",
    run: ({ editor, range }) => block(editor, range, (chain) => chain.setHeading({ level: 2 })),
  },
  {
    title: "Heading 3",
    keywords: "h3",
    icon: Heading3,
    shortcut: keys(MOD, ALT, "3"),
    group: "text",
    run: ({ editor, range }) => block(editor, range, (chain) => chain.setHeading({ level: 3 })),
  },
  {
    title: "Bulleted list",
    keywords: "ul bullets",
    icon: List,
    shortcut: keys(MOD, SHIFT, "8"),
    group: "lists",
    run: ({ editor, range }) => block(editor, range, (chain) => chain.toggleBulletList()),
  },
  {
    title: "Numbered list",
    keywords: "ol numbered",
    icon: ListOrdered,
    shortcut: keys(MOD, SHIFT, "7"),
    group: "lists",
    run: ({ editor, range }) => block(editor, range, (chain) => chain.toggleOrderedList()),
  },
  {
    title: "Checklist",
    keywords: "todo task checkbox",
    icon: ListChecks,
    shortcut: keys(MOD, SHIFT, "9"),
    group: "lists",
    run: ({ editor, range }) => block(editor, range, (chain) => chain.toggleTaskList()),
  },
  {
    title: "Code block",
    keywords: "pre code",
    icon: Code,
    shortcut: keys(MOD, ALT, "C"),
    group: "blocks",
    run: ({ editor, range }) => block(editor, range, (chain) => chain.toggleCodeBlock()),
  },
  {
    title: "Blockquote",
    keywords: "quote",
    icon: Quote,
    shortcut: keys(MOD, SHIFT, "B"),
    group: "blocks",
    run: ({ editor, range }) => block(editor, range, (chain) => chain.toggleBlockquote()),
  },
  {
    title: "Divider",
    keywords: "hr rule line",
    icon: Minus,
    group: "blocks",
    run: ({ editor, range }) => block(editor, range, (chain) => chain.setHorizontalRule()),
  },
];

const slashKey = new PluginKey("slashCommands");

const SlashCommands = Extension.create({
  name: "slashCommands",
  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        char: "/",
        pluginKey: slashKey,
        decorationClass: "slash-query",
        allow: ({ state, range }) => {
          const $pos = state.doc.resolve(range.from);
          for (let depth = $pos.depth; depth > 0; depth -= 1) {
            if ($pos.node(depth).type.name === "codeBlock") return false;
          }
          return true;
        },
        items: ({ query }) => {
          const q = query.trim().toLowerCase();
          if (!q) return SLASH_ITEMS;
          return SLASH_ITEMS.filter(
            (item) => item.title.toLowerCase().includes(q) || item.keywords.includes(q),
          );
        },
        command: ({ editor, range, props }) => {
          props.run({ editor, range });
        },
        render: () => {
          let component: ReactRenderer<SlashMenuHandle> | undefined;
          let unmount: (() => void) | undefined;
          return {
            onStart: (props) => {
              component = new ReactRenderer(SlashMenu, {
                props,
                editor: props.editor,
              });
              component.element.style.zIndex = "50";
              unmount = props.mount(component.element);
            },
            onUpdate: (props) => {
              component?.updateProps(props);
            },
            onKeyDown: (props) => component?.ref?.onKeyDown({ event: props.event }) ?? false,
            onExit: () => {
              unmount?.();
              component?.destroy();
              component = undefined;
            },
          };
        },
      }),
    ];
  },
});

const EDITOR_EXTENSIONS = [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] },
    link: { openOnClick: false, autolink: true },
  }),
  TaskList,
  TaskItem.configure({ nested: true }),
  Image.configure({ allowBase64: true }),
  Placeholder.configure({
    // Each editor names its own hint through `data-empty-hint` on the root element.
    placeholder: ({ editor }) => {
      const attributes = editor.options.editorProps.attributes;
      const hint = typeof attributes === "object" ? attributes["data-empty-hint"] : undefined;
      return hint ?? "Add a description, or type / for commands…";
    },
    emptyEditorClass: "is-editor-empty",
  }),
  Markdown,
  SlashCommands,
];

const EDITOR_PROPS = {
  attributes: {
    class: cn(markdownProse, "markdown-editor min-h-10 outline-none"),
  },
};

type SlashMenuHandle = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

const SlashMenu = forwardRef<SlashMenuHandle, SuggestionProps<SlashItem, SlashItem>>(
  function SlashMenu(props, ref) {
    const [index, setIndex] = useState(0);
    const items = props.items;
    const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

    useEffect(() => {
      // Restart on the first command whenever the filtered list changes.
      setIndex(items.length > 0 ? 0 : 0);
    }, [items]);

    useEffect(() => {
      itemRefs.current[index]?.scrollIntoView({ block: "nearest" });
    }, [index]);

    const select = (item: SlashItem | undefined) => {
      if (item) props.command(item);
    };

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (event.key === "ArrowDown") {
          setIndex((current) => (items.length === 0 ? 0 : (current + 1) % items.length));
          return true;
        }
        if (event.key === "ArrowUp") {
          setIndex((current) => (items.length === 0 ? 0 : (current + items.length - 1) % items.length));
          return true;
        }
        if (event.key === "Enter") {
          select(items[index]);
          return true;
        }
        return false;
      },
    }));

    let previousGroup = "";
    return (
      <div
        role="menu"
        className="max-h-80 w-72 overflow-y-auto rounded-lg border bg-popover p-1 text-popover-foreground shadow-md"
        onMouseDown={(event) => event.preventDefault()}
      >
        {items.length === 0 ? (
          <p className="px-2 py-1.5 text-sm text-muted-foreground">No matching blocks</p>
        ) : (
          items.map((item, itemIndex) => {
            const showDivider = previousGroup !== "" && previousGroup !== item.group;
            previousGroup = item.group;
            const Icon = item.icon;
            return (
              <button
                key={item.title}
                type="button"
                ref={(node) => {
                  itemRefs.current[itemIndex] = node;
                }}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
                  showDivider && "mt-1 border-t pt-2",
                  itemIndex === index ? "bg-accent text-accent-foreground" : "hover:bg-muted",
                )}
                onMouseEnter={() => setIndex(itemIndex)}
                onClick={() => select(item)}
              >
                <Icon className="size-4 text-muted-foreground" />
                <span>{item.title}</span>
                {item.shortcut && (
                  <span className="ml-auto text-xs text-muted-foreground">{item.shortcut}</span>
                )}
              </button>
            );
          })
        )}
      </div>
    );
  },
);

export function normalizeMarkdown(value: string): string {
  return value
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .trim();
}

function setLink(editor: Editor) {
  const previous = editor.getAttributes("link").href as string | undefined;
  const url = window.prompt("Link URL", previous ?? "https://");
  if (url === null) return;
  if (url === "") {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    return;
  }
  editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
}

export function MarkdownEditor({
  initial,
  value,
  onChange,
  hint,
  autoFocus,
  title,
  label = "Description",
  placeholder,
}: {
  initial: string;
  /** Replaces the document when it differs from what the editor last emitted. */
  value?: string;
  onChange: (markdown: string) => void;
  hint?: string;
  autoFocus?: boolean;
  /** Section heading shown on the toolbar row, e.g. "Description". */
  title?: ReactNode;
  /** Accessible name for the editable area. */
  label?: string;
  /** Shown in the empty editor. */
  placeholder?: string;
}) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const ready = useRef(false);
  const content = useRef(initial).current;
  const lastEmitted = useRef(normalizeMarkdown(content));
  const editorProps = useMemo(
    () => ({
      attributes: {
        ...EDITOR_PROPS.attributes,
        "aria-label": label,
        ...(placeholder ? { "data-empty-hint": placeholder } : {}),
      },
    }),
    [label, placeholder],
  );

  const editor = useEditor({
    extensions: EDITOR_EXTENSIONS,
    content,
    contentType: "markdown",
    autofocus: autoFocus ? "end" : false,
    editorProps,
    onCreate: ({ editor: created }) => {
      lastEmitted.current = normalizeMarkdown(created.getMarkdown());
      queueMicrotask(() => {
        ready.current = true;
      });
    },
    onUpdate: ({ editor: current, transaction }) => {
      if (!ready.current || !transaction.docChanged) return;
      const next = normalizeMarkdown(current.getMarkdown());
      if (next === lastEmitted.current) return;
      lastEmitted.current = next;
      onChangeRef.current(next);
    },
  });

  useEffect(() => {
    if (!editor || value === undefined) return;
    const next = normalizeMarkdown(value);
    if (next === lastEmitted.current) return;
    lastEmitted.current = next;
    editor.commands.setContent(value, { emitUpdate: false, contentType: "markdown" });
  }, [editor, value]);

  const active = useEditorState({
    editor,
    selector: ({ editor: current }) =>
      current
        ? {
            bold: current.isActive("bold"),
            italic: current.isActive("italic"),
            strike: current.isActive("strike"),
            code: current.isActive("code"),
            h1: current.isActive("heading", { level: 1 }),
            h2: current.isActive("heading", { level: 2 }),
            h3: current.isActive("heading", { level: 3 }),
            bullet: current.isActive("bulletList"),
            ordered: current.isActive("orderedList"),
            task: current.isActive("taskList"),
            quote: current.isActive("blockquote"),
            codeBlock: current.isActive("codeBlock"),
            link: current.isActive("link"),
          }
        : null,
  });

  if (!editor || !active) return null;

  const mark = (label: string, icon: LucideIcon, pressed: boolean, run: () => void) => (
    <Toggle
      key={label}
      size="sm"
      pressed={pressed}
      aria-label={label}
      title={label}
      onMouseDown={(event) => event.preventDefault()}
      onPressedChange={run}
    >
      {icon === Heading1 || icon === Heading2 || icon === Heading3 ? (
        <span className="text-xs font-semibold">{label.replace("Heading ", "H")}</span>
      ) : (
        <IconWrap icon={icon} />
      )}
    </Toggle>
  );

  // The editable area is borderless until the pointer or focus is on it, and the formatting
  // toolbar sits on the heading row, revealed the same way. Coarse pointers always see it.
  return (
    <section className="group/rte">
      <div className="flex min-h-7 flex-wrap items-center gap-x-2 gap-y-1">
        {title && <h2 className="text-sm font-semibold tracking-tight">{title}</h2>}
        {hint && <span className="text-xs text-muted-foreground transition-opacity">{hint}</span>}
        <div
          className="no-scrollbar ml-auto flex max-w-full items-center gap-0.5 overflow-x-auto opacity-0 transition-opacity duration-(--motion-dur) group-focus-within/rte:opacity-100 group-hover/rte:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100 [&>*]:shrink-0"
          role="toolbar"
          aria-label="Formatting"
        >
          {mark("Heading 1", Heading1, active.h1, () =>
            editor.chain().focus().toggleHeading({ level: 1 }).run(),
          )}
          {mark("Heading 2", Heading2, active.h2, () =>
            editor.chain().focus().toggleHeading({ level: 2 }).run(),
          )}
          {mark("Heading 3", Heading3, active.h3, () =>
            editor.chain().focus().toggleHeading({ level: 3 }).run(),
          )}
          <ToolbarDivider />
          {mark("Bold", Bold, active.bold, () => editor.chain().focus().toggleBold().run())}
          {mark("Italic", Italic, active.italic, () => editor.chain().focus().toggleItalic().run())}
          {mark("Strikethrough", Strikethrough, active.strike, () =>
            editor.chain().focus().toggleStrike().run(),
          )}
          {mark("Inline code", Code, active.code, () => editor.chain().focus().toggleCode().run())}
          <ToolbarDivider />
          {mark("Bulleted list", List, active.bullet, () => editor.chain().focus().toggleBulletList().run())}
          {mark("Numbered list", ListOrdered, active.ordered, () =>
            editor.chain().focus().toggleOrderedList().run(),
          )}
          {mark("Checklist", ListChecks, active.task, () => editor.chain().focus().toggleTaskList().run())}
          <ToolbarDivider />
          {mark("Blockquote", Quote, active.quote, () => editor.chain().focus().toggleBlockquote().run())}
          {mark("Code block", SquareCode, active.codeBlock, () =>
            editor.chain().focus().toggleCodeBlock().run(),
          )}
          {mark("Link", Link2, active.link, () => setLink(editor))}
        </div>
      </div>
      <div className="-mx-2.5 mt-1 rounded-lg border border-transparent px-2.5 py-1.5 transition-colors duration-(--motion-dur) group-focus-within/rte:border-input group-hover/rte:border-border">
        <EditorContent editor={editor} />
      </div>
    </section>
  );
}

function IconWrap({ icon: Icon }: { icon: LucideIcon }) {
  return <Icon />;
}

function ToolbarDivider() {
  return <span className="mx-1 h-4 w-px bg-border" />;
}
